import React, { useEffect, useRef, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { isSessionExpiredError } from '../../../pages/domestic/domesticAuthHttp';
import { setUser } from '../../../utils/actions';
import styles from './FindUserByOrgDialog.module.css';
import {
  type CpUser,
  type OrgMatch,
  searchActiveUsers,
  searchOrganizations,
} from './findUserByOrgApi';

const ORG_SEARCH_DELAY_MS = 300;
const MIN_ORG_CHARS = 3;
// Orgs with more people than this get a filter box above the list.
const FILTER_THRESHOLD = 8;

type UserState = {
  user: {
    username?: string;
    tokenType?: string;
    idToken?: string;
  };
};

type Purpose = 'assume' | 'search';

function personName(person: CpUser) {
  const name = `${person.firstName || ''} ${person.lastName || ''}`.trim();
  return name || person.webId || '';
}

const BuildingIcon = () => (
  <svg className={styles.icon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3 21h18" />
    <path d="M5 21V7l8-4v18" />
    <path d="M19 21V11l-6-4" />
    <path d="M9 9v.01M9 12v.01M9 15v.01M9 18v.01" />
  </svg>
);

const ChevronIcon = () => (
  <svg className={styles.chevron} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="m9 6 6 6-6 6" />
  </svg>
);

const CheckIcon = () => (
  <svg className={styles.icon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="m5 12 5 5L20 7" />
  </svg>
);

const BackIcon = () => (
  <svg className={styles.icon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M19 12H5M11 18l-6-6 6-6" />
  </svg>
);

/** "Look up by organization" link. Place it on the email field's label row. */
export function FindUserByOrgLink({ onClick }: { onClick: () => void }) {
  const { t } = useTranslation();
  return (
    <button type="button" className={styles.textLink} onClick={onClick}>
      <BuildingIcon />
      {t('findUserByOrg.link')}
    </button>
  );
}

/** Confirmation under the email field after the dialog fills it in. */
export function FindUserByOrgFilledNote({
  organizationName,
  purpose = 'assume',
}: {
  organizationName: string;
  purpose?: Purpose;
}) {
  const { t } = useTranslation();
  if (!organizationName) return null;
  return (
    <span className={styles.filledNote} role="status">
      <CheckIcon />
      <span>
        {t(purpose === 'search' ? 'findUserByOrg.filledFromSearch' : 'findUserByOrg.filledFrom', {
          organization: organizationName,
        })}
      </span>
    </span>
  );
}

function MatchedName({ name, query }: { name: string; query: string }) {
  const n = query.trim().length;
  if (!n || !name.toLowerCase().startsWith(query.trim().toLowerCase())) return <>{name}</>;
  return (
    <>
      <mark className={styles.match}>{name.slice(0, n)}</mark>
      {name.slice(n)}
    </>
  );
}

/**
 * Two steps: pick an organization (prefix typeahead), then pick one of its
 * active users. Choosing a person calls `onSelect(webId, organizationName)`
 * and does not submit anything.
 */
export default function FindUserByOrgDialog({
  show,
  onClose,
  onSelect,
  purpose = 'assume',
}: {
  show: boolean;
  onClose: () => void;
  onSelect: (webId: string, organizationName: string) => void;
  purpose?: Purpose;
}) {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();
  const user = useSelector((state: UserState) => state.user);
  const orgInputRef = useRef<HTMLInputElement>(null);
  const [orgQuery, setOrgQuery] = useState('');
  const [orgs, setOrgs] = useState<OrgMatch[]>([]);
  const [orgState, setOrgState] = useState<'idle' | 'searching' | 'done' | 'error'>('idle');
  const [activeIndex, setActiveIndex] = useState(-1);
  const [retryCount, setRetryCount] = useState(0);
  const [selectedOrg, setSelectedOrg] = useState<OrgMatch | null>(null);
  const [users, setUsers] = useState<CpUser[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [userState, setUserState] = useState<'idle' | 'searching' | 'done' | 'error'>('idle');
  const [filter, setFilter] = useState('');

  const authOptions = {
    onTokenRefreshed: (updatedUser: object) => dispatch(setUser(updatedUser)),
    onSessionExpired: () => history.push({ pathname: '/signin', state: { sessionExpired: true } }),
  };

  useEffect(() => {
    if (show) return undefined;
    setOrgQuery('');
    setOrgs([]);
    setOrgState('idle');
    setActiveIndex(-1);
    setSelectedOrg(null);
    setUsers([]);
    setTotalRecords(0);
    setUserState('idle');
    setFilter('');
    return undefined;
  }, [show]);

  useEffect(() => {
    if (!show || selectedOrg) return undefined;
    const trimmed = orgQuery.trim();
    if (trimmed.length < MIN_ORG_CHARS) {
      setOrgs([]);
      setOrgState('idle');
      return undefined;
    }

    let cancelled = false;
    const timer = setTimeout(() => {
      setOrgState('searching');
      searchOrganizations(trimmed, user, authOptions)
        .then((rows) => {
          if (cancelled) return;
          setOrgs(rows);
          setActiveIndex(rows.length ? 0 : -1);
          setOrgState('done');
        })
        .catch((err: unknown) => {
          if (cancelled || isSessionExpiredError(err)) return;
          setOrgs([]);
          setOrgState('error');
        });
    }, ORG_SEARCH_DELAY_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // authOptions is rebuilt each render; the search should follow the query.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgQuery, selectedOrg, show, retryCount]);

  useEffect(() => {
    if (!show || !selectedOrg) return undefined;
    let cancelled = false;
    setUserState('searching');
    setUsers([]);
    searchActiveUsers(selectedOrg.organizationId, user, authOptions)
      .then((result) => {
        if (cancelled) return;
        setUsers(result.users);
        setTotalRecords(result.totalRecords);
        setUserState('done');
      })
      .catch((err: unknown) => {
        if (cancelled || isSessionExpiredError(err)) return;
        setUsers([]);
        setUserState('error');
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrg, show]);

  const pickOrg = (org: OrgMatch) => {
    setFilter('');
    setSelectedOrg(org);
  };

  const backToOrgs = () => {
    setSelectedOrg(null);
    setUsers([]);
    setTotalRecords(0);
    setUserState('idle');
    setFilter('');
    setTimeout(() => orgInputRef.current?.focus(), 0);
  };

  const onOrgKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!orgs.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(orgs.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter' && activeIndex >= 0) {
      e.preventDefault();
      pickOrg(orgs[activeIndex]);
    }
  };

  const filterText = filter.trim().toLowerCase();
  const visibleUsers = filterText
    ? users.filter(
        (person) =>
          personName(person).toLowerCase().includes(filterText) ||
          `${person.webId || ''}`.toLowerCase().includes(filterText),
      )
    : users;
  const showPartial = users.length > 0 && totalRecords > users.length;

  const renderOrgStep = () => (
    <>
      <div className={styles.field}>
        <label className={styles.fieldLabel} htmlFor="findUserByOrgInput">
          {t('findUserByOrg.labelOrganization')}
        </label>
        <input
          id="findUserByOrgInput"
          ref={orgInputRef}
          className={styles.fieldInput}
          value={orgQuery}
          placeholder={t('findUserByOrg.placeholderOrganization')}
          autoComplete="off"
          spellCheck={false}
          role="combobox"
          aria-expanded={orgs.length > 0}
          aria-controls="findUserByOrgList"
          aria-activedescendant={activeIndex >= 0 && orgs[activeIndex] ? `findUserByOrgOption${orgs[activeIndex].organizationId}` : undefined}
          aria-describedby="findUserByOrgHint"
          onKeyDown={onOrgKeyDown}
          onChange={(e) => setOrgQuery(e.target.value)}
        />
        <span id="findUserByOrgHint" className={styles.helpText}>
          {t('findUserByOrg.hintMinChars')}
        </span>
      </div>

      {orgState === 'searching' ? (
        <div className={styles.statusMessage}>
          <span className={styles.spinner} aria-hidden="true" />
          {t('findUserByOrg.searchingOrganizations')}
        </div>
      ) : null}
      {orgState === 'error' ? (
        <div className={styles.searchError} role="alert">
          <span>{t('findUserByOrg.searchFailed')}</span>
          <button type="button" className={styles.inlineLink} onClick={() => setRetryCount((n) => n + 1)}>
            {t('findUserByOrg.btnRetry')}
          </button>
        </div>
      ) : null}
      {orgState === 'done' && orgs.length === 0 ? (
        <div className={styles.statusMessage}>
          {t('findUserByOrg.noOrganizations', { query: orgQuery.trim() })}
        </div>
      ) : null}

      {orgs.length > 0 ? (
        <div className={styles.list} id="findUserByOrgList" role="listbox" aria-label={t('findUserByOrg.labelOrganization')}>
          {orgs.map((org, i) => (
            <button
              type="button"
              role="option"
              aria-selected={i === activeIndex}
              id={`findUserByOrgOption${org.organizationId}`}
              key={org.organizationId}
              className={[styles.listRow, i === activeIndex ? styles.listRowActive : ''].join(' ')}
              onMouseEnter={() => setActiveIndex(i)}
              onClick={() => pickOrg(org)}
            >
              <span className={styles.rowMain}>
                <span className={styles.rowName}>
                  <MatchedName name={org.organizationName} query={orgQuery} />
                </span>
              </span>
              <span className={styles.rowMeta}>
                {t('findUserByOrg.orgId', { id: org.organizationId })}
              </span>
              <ChevronIcon />
            </button>
          ))}
        </div>
      ) : null}
    </>
  );

  const renderUserStep = (org: OrgMatch) => (
    <>
      <div className={styles.orgChip}>
        <BuildingIcon />
        <span className={styles.orgChipName}>
          {org.organizationName}{' '}
          <span className={styles.orgChipId}>{t('findUserByOrg.orgId', { id: org.organizationId })}</span>
        </span>
        <button type="button" className={styles.inlineLink} onClick={backToOrgs}>
          {t('findUserByOrg.btnChange')}
        </button>
      </div>

      {userState === 'searching' ? (
        <div className={styles.statusMessage}>
          <span className={styles.spinner} aria-hidden="true" />
          {t('findUserByOrg.loadingUsers')}
        </div>
      ) : null}
      {userState === 'error' ? (
        <div className={styles.searchError} role="alert">
          <span>{t('findUserByOrg.usersFailed')}</span>
        </div>
      ) : null}
      {userState === 'done' && users.length === 0 ? (
        <div className={styles.statusMessage}>{t('findUserByOrg.noUsers')}</div>
      ) : null}

      {users.length > 0 ? (
        <>
          <div className={styles.statusMessage}>
            {t('findUserByOrg.activeUsersCount', { count: totalRecords })}
          </div>
          {users.length > FILTER_THRESHOLD ? (
            <input
              id="findUserByOrgFilter"
              className={styles.fieldInput}
              value={filter}
              placeholder={t('findUserByOrg.placeholderFilter')}
              aria-label={t('findUserByOrg.placeholderFilter')}
              autoComplete="off"
              spellCheck={false}
              onChange={(e) => setFilter(e.target.value)}
            />
          ) : null}
          <div className={[styles.list, styles.listTall].join(' ')} role="list">
            {visibleUsers.length === 0 ? (
              <div className={styles.emptyRow}>{t('findUserByOrg.noFilterMatch', { query: filter.trim() })}</div>
            ) : (
              visibleUsers.map((person) => (
                <button
                  type="button"
                  role="listitem"
                  key={person.webId}
                  className={styles.listRow}
                  onClick={() => person.webId && onSelect(person.webId, org.organizationName)}
                >
                  <span className={styles.rowMain}>
                    <span className={styles.rowName}>{personName(person)}</span>
                    <span className={styles.rowSub}>{person.webId}</span>
                  </span>
                  <ChevronIcon />
                </button>
              ))
            )}
          </div>
          {showPartial ? (
            <div className={styles.helpText}>
              {t('findUserByOrg.showingPartial', { count: users.length, total: totalRecords })}
            </div>
          ) : null}
        </>
      ) : null}
    </>
  );

  return (
    <Modal
      show={show}
      onHide={onClose}
      onEntered={() => orgInputRef.current?.focus()}
      backdrop="static"
      centered
      dialogClassName="findUserByOrgModal"
      aria-labelledby="findUserByOrgTitle"
    >
      <Modal.Header>
        <div className={styles.dialogHeader}>
          <div className={styles.dialogTitles}>
            <div className={styles.stepLabel}>
              {selectedOrg ? t('findUserByOrg.stepPerson') : t('findUserByOrg.stepOrganization')}
            </div>
            <h2 className={styles.dialogTitle} id="findUserByOrgTitle">
              {t('findUserByOrg.title')}
            </h2>
            <p className={styles.dialogSubtitle}>
              {t(purpose === 'search' ? 'findUserByOrg.subtitleSearch' : 'findUserByOrg.subtitleAssume')}
            </p>
          </div>
          <button
            type="button"
            className={styles.dialogCloseBtn}
            aria-label={t('findUserByOrg.close')}
            onClick={onClose}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
              <path
                d="M1.4 14L0 12.6L5.6 7L0 1.4L1.4 0L7 5.6L12.6 0L14 1.4L8.4 7L14 12.6L12.6 14L7 8.4L1.4 14Z"
                fill="currentColor"
              />
            </svg>
          </button>
        </div>
      </Modal.Header>
      <Modal.Body>
        <div className={styles.dialogBody}>
          {selectedOrg ? renderUserStep(selectedOrg) : renderOrgStep()}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className={styles.dialogFooter}>
          {selectedOrg ? (
            <button type="button" className={[styles.cancelBtn, styles.backBtn].join(' ')} onClick={backToOrgs}>
              <BackIcon />
              {t('findUserByOrg.btnBack')}
            </button>
          ) : null}
          <button type="button" className={styles.cancelBtn} onClick={onClose}>
            {t('findUserByOrg.btnCancel')}
          </button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}
